import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { navigateToTarget } from '../../../../utils/navigateToTarget';
import { useTranslation } from 'react-i18next';
import { XIcon } from '../../../../assets/X';
import { homeAPI } from '../../api/homeAPI';

interface StoryItem {
  id: number;
  type: 'video' | 'image';
  url: string;
  sortOrder: number;
  duration: number;
  target?: string;
  buttonText?: string;
}

interface StoryGroup {
  id: number;
  title: string;
  preview: string;
  sortOrder: number;
  items: StoryItem[];
}

interface StoriesResponse {
  success: boolean;
  data: StoryGroup[];
}

const STORAGE_KEY = 'viewedStories';

function getViewedIds(): Set<number> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as number[]);
  } catch {
    return new Set();
  }
}

function saveViewedIds(ids: Set<number>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify([...ids]));
}

function PreviewImage({ src, alt }: { src: string; alt: string }) {
  const [hasError, setHasError] = useState(false);

  if (hasError) {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-neutral-100">
        <svg className="w-7 h-7 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3 3l18 18" />
        </svg>
        <span className="text-[10px] text-neutral-400 font-medium leading-tight text-center px-1">
          {alt}
        </span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className="absolute inset-0 h-full w-full object-cover"
      onError={() => setHasError(true)}
    />
  );
}

export function Stories({ branchId }: { branchId?: number }) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'uz');
  const [groups, setGroups] = useState<StoryGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [activeGroupIndex, setActiveGroupIndex] = useState<number | null>(null);
  const [activeChildIndex, setActiveChildIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [viewedIds, setViewedIds] = useState<Set<number>>(new Set());
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [mediaError, setMediaError] = useState(false);
  const [mediaLoading, setMediaLoading] = useState(true);
  const [retryTimestamp, setRetryTimestamp] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);

  const pointerStartY = useRef<number | null>(null);
  const pointerStartX = useRef<number | null>(null);
  const pointerStartTime = useRef<number>(0);
  const isDraggingRef = useRef<boolean>(false);
  const hasMovedRef = useRef<boolean>(false);

  const activeGroup = activeGroupIndex === null ? undefined : groups[activeGroupIndex];
  const activeItem = activeGroup ? activeGroup.items[activeChildIndex] : undefined;

  const mediaUrl = activeItem
    ? activeItem.url +
      (retryTimestamp
        ? `${activeItem.url.includes('?') ? '&' : '?'}t=${retryTimestamp}`
        : '')
    : '';

  useEffect(() => {
    setDragY(0);
    setIsDragging(false);
    isDraggingRef.current = false;
    pointerStartY.current = null;
    setMediaError(false);
    setMediaLoading(true);
    setRetryTimestamp(0);
    setProgress(0); // Reset progress when item changes
  }, [activeGroupIndex, activeChildIndex]);

  useEffect(() => {
    setViewedIds(getViewedIds());
  }, []);

  useEffect(() => {
    const handler = (lng: string) => setLanguage(lng);
    i18n.on('languageChanged', handler);
    return () => { i18n.off('languageChanged', handler); };
  }, [i18n]);

  useEffect(() => {
    // Филиал может быть неизвестен (нет адреса, геолокация выключена) — тогда
    // запрашиваем без branch_id и показываем общую подборку.
    let isCurrent = true;
    setIsLoading(true);
    setIsError(false);
    setGroups([]);

    homeAPI.getStories(language, branchId).then((res) => {
      if (!isCurrent) return;
      const data = (res.data as StoriesResponse).data;
      const sorted = [...data].sort((a, b) => a.sortOrder - b.sortOrder).map(group => ({
        ...group,
        items: [...group.items].sort((a, b) => a.sortOrder - b.sortOrder),
      }));
      setGroups(sorted);

      const allItemIds = new Set(sorted.flatMap(g => g.items.map(i => i.id)));
      setViewedIds(prev => {
        const cleaned = new Set([...prev].filter(id => allItemIds.has(id)));
        if (cleaned.size !== prev.size) {
          saveViewedIds(cleaned);
        }
        return cleaned;
      });
    }).catch(() => {
      if (isCurrent) {
        setGroups([]);
        setIsError(true);
      }
    }).finally(() => {
      if (isCurrent) setIsLoading(false);
    });

    return () => {
      isCurrent = false;
    };
  }, [language, retryCount, branchId]);

  useEffect(() => {
    if (activeGroupIndex === null) return;
    const nextGroupIndex = activeGroupIndex + 1;
    const groupsToPreload = [activeGroupIndex, nextGroupIndex].filter(i => i < groups.length);
    groupsToPreload.forEach(gi => {
      groups[gi].items.forEach(item => {
        if (item.type === 'image') {
          const image = new Image();
          image.src = item.url;
        }
      });
    });
  }, [groups, activeGroupIndex]);

  useEffect(() => {
    if (activeGroupIndex === null || isPaused || mediaError || !activeGroup || !activeItem) return;

    const duration = activeItem.duration * 1000;
    const startedAt = performance.now() - (progress / 100) * duration;
    let animationFrame = 0;

    const animate = (timestamp: number) => {
      const nextProgress = Math.min(((timestamp - startedAt) / duration) * 100, 100);
      setProgress(nextProgress);

      if (nextProgress >= 100) {
        if (activeItem && !viewedIds.has(activeItem.id)) {
          const next = new Set(viewedIds);
          next.add(activeItem.id);
          setViewedIds(next);
          saveViewedIds(next);
        }
        if (activeChildIndex < activeGroup.items.length - 1) {
          setActiveChildIndex(currentIndex => currentIndex + 1);
          setProgress(0);
        } else if (activeGroupIndex < groups.length - 1) {
          setActiveGroupIndex(currentIndex => (currentIndex === null ? 0 : currentIndex + 1));
          setActiveChildIndex(0);
          setProgress(0);
        } else {
          setActiveGroupIndex(null);
          setActiveChildIndex(0);
          setProgress(0);
        }
        return;
      }

      animationFrame = requestAnimationFrame(animate);
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [activeGroupIndex, activeChildIndex, isPaused, mediaError]);

  useEffect(() => {
    if (activeGroupIndex === null || !activeItem) return;
    if (activeItem.type === 'video' && videoRef.current) {
      if (isPaused || mediaLoading || mediaError) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(() => {});
      }
    }
  }, [activeGroupIndex, activeChildIndex, isPaused, mediaLoading, mediaError]);

  useEffect(() => {
    if (activeGroupIndex === null || !activeItem) return;
    if (activeItem.type === 'video' && videoRef.current) {
      videoRef.current.currentTime = 0;
    }
  }, [activeGroupIndex, activeChildIndex]);

  const openGroup = (index: number) => {
    const group = groups[index];
    const firstUnviewed = group ? group.items.findIndex(item => !viewedIds.has(item.id)) : -1;
    const startIndex = firstUnviewed === -1 ? 0 : firstUnviewed;
    if (group) {
      markItemViewed(group.items[startIndex].id);
    }
    setActiveGroupIndex(index);
    setActiveChildIndex(startIndex);
    setProgress(0);
    setIsPaused(false);
  };

  const markItemViewed = (itemId: number) => {
    if (viewedIds.has(itemId)) return;
    const next = new Set(viewedIds);
    next.add(itemId);
    setViewedIds(next);
    saveViewedIds(next);
  };

  const closeStory = () => {
    if (activeItem) {
      markItemViewed(activeItem.id);
    }
    setActiveGroupIndex(null);
    setActiveChildIndex(0);
    setProgress(0);
    setIsPaused(false);
    setDragY(0);
    setIsDragging(false);
    isDraggingRef.current = false;
    pointerStartY.current = null;
    dragYRef.current = 0;
  };

  const dragYRef = useRef<number>(0);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // Ignore close-button and action-button clicks
    const target = e.target as HTMLElement;
    if (target.closest('[data-close-btn]') || target.closest('[data-action-btn]')) return;

    pointerStartY.current = e.clientY;
    pointerStartX.current = e.clientX;
    pointerStartTime.current = Date.now();
    isDraggingRef.current = false;
    hasMovedRef.current = false;
    dragYRef.current = 0;

    setIsPaused(true);
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {}
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (pointerStartY.current === null || pointerStartX.current === null) return;

    const deltaY = e.clientY - pointerStartY.current;
    const deltaX = e.clientX - pointerStartX.current;

    if (Math.abs(deltaY) > 8 || Math.abs(deltaX) > 8) {
      hasMovedRef.current = true;
    }

    // Start drag only when moving clearly downward and more vertical than horizontal
    if (deltaY > 8 && Math.abs(deltaY) > Math.abs(deltaX) * 1.2) {
      isDraggingRef.current = true;
      dragYRef.current = deltaY;
      setIsDragging(true);
      setDragY(deltaY);
    } else if (!isDraggingRef.current) {
      dragYRef.current = 0;
      setDragY(0);
    }
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (pointerStartY.current === null) return;

    try {
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
    } catch {}

    const duration = Date.now() - pointerStartTime.current;
    // Read from ref — always fresh, no stale closure
    const currentDragY = dragYRef.current;
    const wasDragging = isDraggingRef.current;

    pointerStartY.current = null;
    isDraggingRef.current = false;

    if (wasDragging) {
      const velocity = duration > 0 ? currentDragY / duration : 0;
      if (currentDragY > 80 || velocity > 0.35) {
        closeStory();
      } else {
        dragYRef.current = 0;
        setDragY(0);
        setIsDragging(false);
        setIsPaused(false);
      }
    } else {
      setIsPaused(false);
      // Tap navigation (only if barely moved and quick tap)
      if (!hasMovedRef.current && duration < 350) {
        const clickX = e.clientX;
        const screenWidth = window.innerWidth;
        if (clickX < screenWidth * 0.35) {
          moveStory(-1);
        } else {
          moveStory(1);
        }
      }
    }
  };

  const handlePointerCancel = (e: React.PointerEvent<HTMLDivElement>) => {
    if (pointerStartY.current === null) return;
    try {
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
    } catch {}
    pointerStartY.current = null;
    isDraggingRef.current = false;
    dragYRef.current = 0;
    setDragY(0);
    setIsDragging(false);
    setIsPaused(false);
  };

  const moveStory = (direction: -1 | 1) => {
    if (activeGroupIndex === null || !activeGroup) return;

    if (activeItem) {
      markItemViewed(activeItem.id);
    }

    if (direction === -1) {
      if (activeChildIndex > 0) {
        setActiveChildIndex(activeChildIndex - 1);
        setProgress(0);
      } else if (activeGroupIndex > 0) {
        const prevGroup = groups[activeGroupIndex - 1];
        setActiveGroupIndex(activeGroupIndex - 1);
        setActiveChildIndex(prevGroup.items.length - 1);
        setProgress(0);
      }
    } else {
      if (activeChildIndex < activeGroup.items.length - 1) {
        setActiveChildIndex(activeChildIndex + 1);
        setProgress(0);
      } else if (activeGroupIndex < groups.length - 1) {
        setActiveGroupIndex(activeGroupIndex + 1);
        setActiveChildIndex(0);
        setProgress(0);
      } else {
        closeStory();
      }
    }
  };

  useEffect(() => {
    if (activeGroupIndex === null) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') moveStory(-1);
      if (event.key === 'ArrowRight') moveStory(1);
      if (event.key === 'Escape') closeStory();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeGroupIndex, activeChildIndex]);

  const retryFetch = () => {
    setRetryCount(prev => prev + 1);
  };

  // Нечего показывать — не рисуем секцию вовсе, иначе от неё остаётся
  // пустой отступ в ленте.
  if (!isLoading && !isError && groups.length === 0) return null;

  return (
    <>
      <section className="w-full overflow-hidden" aria-label="Stories">
        {isError ? (
          <div className="flex flex-col items-center justify-center gap-3 px-4 py-6">
            <p className="text-body-sm-medium text-text-secondary">Не удалось загрузить Stories</p>
            <button
              type="button"
              onClick={retryFetch}
              className="rounded-full bg-brand-green px-5 py-2 text-body-sm-semibold text-white"
            >
              Повторить
            </button>
          </div>
        ) : (
          <div className="flex gap-[6px] overflow-x-auto px-4 py-1 scrollbar-none">
          {isLoading && (
            <>
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="relative h-[116px] w-[96px] min-w-[96px] shrink-0 animate-pulse rounded-[16px] bg-gray-200"
                >
                  <div className="absolute bottom-3 left-2 right-2 space-y-1.5">
                    <div className="h-2.5 w-3/4 rounded-full bg-gray-300" />
                    <div className="h-2.5 w-1/2 rounded-full bg-gray-300" />
                  </div>
                </div>
              ))}
            </>
          )}
          {groups.map((group, groupIndex) => {
            return (
              <button
                key={group.id}
                type="button"
                onClick={() => openGroup(groupIndex)}
                className="relative h-[116px] w-[96px] min-w-[96px] shrink-0 overflow-hidden rounded-[16px] p-0 text-left shadow-none transition-all"
              >
                {group.preview.endsWith('.mp4') ? (
                  <video
                    src={group.preview}
                    poster={group.items.find(item => item.type === 'image')?.url}
                    muted
                    playsInline
                    preload="metadata"
                    onLoadedMetadata={event => {
                      const video = event.currentTarget;
                      video.muted = true;
                      video.currentTime = 1;
                    }}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                ) : (
                  <PreviewImage src={group.preview} alt={group.title} />
                )}
                

                
                {/* Bottom segments - one per story item */}
                {group.items.length > 1 && (
                  <div className="absolute bottom-0 left-0 right-0 flex h-[41px] z-[2]">
                    {group.items.map((_, childIndex) => (
                      <span
                        key={childIndex}
                        className="flex-1"
                      />
                    ))}
                  </div>
                )}
                
                {(() => {
                  const n = group.items.length;

                  // Geometry for viewBox 0 0 88 108, r=11.5
                  // Path starts at M44 0.5 = top-center
                  // Side lengths: top=65, right=85, bottom=65, left=85
                  // Corner arc = π/2 * 11.5 ≈ 18.064
                  const ARC   = Math.PI / 2 * 11.5;  // ≈ 18.064
                  const TOP   = 65;
                  const RIGHT = 85;
                  const BOT   = 65;
                  const LEFT  = 85;
                  const P     = TOP + RIGHT + BOT + LEFT + 4 * ARC; // ≈ 372.256

                  // Distance from start (top-center) to each side's midpoint
                  const MID_TOP    = 0;                                              // 0
                  const MID_RIGHT  = TOP / 2 + ARC + RIGHT / 2;                    // ≈ 93.064
                  const MID_BOTTOM = TOP / 2 + ARC + RIGHT + ARC + BOT / 2;        // ≈ 186.128
                  const MID_LEFT   = TOP / 2 + ARC + RIGHT + ARC + BOT + ARC + LEFT / 2; // ≈ 279.192

                  // For each n, pick which gap-points to use (evenly spaced mid-points)
                  const gapPoints: number[] = [];
                  if (n >= 2) gapPoints.push(MID_TOP, MID_BOTTOM);
                  if (n >= 3) gapPoints.push(MID_RIGHT);
                  if (n >= 4) gapPoints.push(MID_LEFT);
                  // For n>4 fall back to even spacing from top-center
                  if (n > 4) {
                    gapPoints.length = 0;
                    for (let i = 0; i < n; i++) gapPoints.push(i * (P / n));
                  }

                  const GAP = 4;

                  // If only 1 item, draw a solid ring with no gaps
                  if (n === 1) {
                    const item = group.items[0];
                    return (
                      <svg
                        className="pointer-events-none absolute z-[3]"
                        style={{ left: '4px', top: '4px' }}
                        width="88"
                        height="108"
                        viewBox="0 0 88 108"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M44 0.5H76A11.5 11.5 0 0 1 87.5 12V96A11.5 11.5 0 0 1 76 107.5H12A11.5 11.5 0 0 1 0.5 96V12A11.5 11.5 0 0 1 12 0.5H44"
                          stroke={viewedIds.has(item.id) ? '#FFFFFF' : 'rgba(255,255,255,0.4)'}
                          strokeWidth="1.5"
                        />
                      </svg>
                    );
                  }

                  // Sort gap points and build dash segments between them
                  const pts = [...gapPoints].sort((a, b) => a - b);
                  // Build segments: from pts[i]+GAP/2 to pts[i+1]-GAP/2, wrapping around
                  const segments: { offset: number; length: number }[] = [];
                  for (let i = 0; i < pts.length; i++) {
                    const start = pts[i] + GAP / 2;
                    const end   = (i + 1 < pts.length ? pts[i + 1] : pts[0] + P) - GAP / 2;
                    segments.push({ offset: start, length: end - start });
                  }

                  return (
                    <svg
                      className="pointer-events-none absolute z-[3]"
                      style={{ left: '4px', top: '4px' }}
                      width="88"
                      height="108"
                      viewBox="0 0 88 108"
                      fill="none"
                      aria-hidden="true"
                    >
                      {group.items.map((item, i) => {
                        const seg = segments[i % segments.length];
                        // strokeDasharray: one visible dash of `seg.length`, rest invisible
                        // strokeDashoffset: negative offset so dash starts at seg.offset
                        return (
                          <path
                            key={item.id}
                            d="M44 0.5H76A11.5 11.5 0 0 1 87.5 12V96A11.5 11.5 0 0 1 76 107.5H12A11.5 11.5 0 0 1 0.5 96V12A11.5 11.5 0 0 1 12 0.5H44"
                            stroke={viewedIds.has(item.id) ? '#FFFFFF' : 'rgba(255,255,255,0.4)'}
                            strokeWidth="1.5"
                            strokeDasharray={`${seg.length} ${P - seg.length}`}
                            strokeDashoffset={-seg.offset}
                          />
                        );
                      })}
                    </svg>
                  );
                })()}
                
                <span className="absolute bottom-[10px] left-[10px] right-2 line-clamp-2 text-[12px] font-inter font-semibold leading-[14px] tracking-normal text-white z-[10]">
                  {group.title}
                </span>
              </button>
            );
          })}
          </div>
        )}
      </section>

      {activeGroupIndex !== null && activeGroup && activeItem && (
        <div
          data-blocks-pull-refresh
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 touch-none select-none"
          style={{
            opacity: isDragging ? Math.max(0.1, 1 - dragY / 400) : 1,
            transition: isDragging ? 'none' : 'opacity 0.2s ease-out',
          }}
          onClick={closeStory}
        >
          <div
            className="relative h-full w-full overflow-hidden bg-black touch-none select-none flex flex-col justify-between"
            style={{
              transform: `translate3d(0, ${Math.max(0, dragY)}px, 0) scale(${isDragging ? Math.max(0.85, 1 - dragY / 1200) : 1})`,
              borderRadius: isDragging ? `${Math.min(dragY / 4, 24)}px` : '0px',
              transition: isDragging ? 'none' : 'transform 0.2s ease-out, border-radius 0.2s ease-out',
            }}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerCancel}
          >
            {/* Top header section: Progress bar + Story info + Close button */}
            <div
              className="absolute left-0 right-0 z-20 flex flex-col gap-2.5 px-4 pointer-events-none"
              style={{
                top: 'calc(max(var(--tg-content-safe-area-inset-top, 0px), calc(var(--tg-safe-area-inset-top, 0px) + 44px), calc(env(safe-area-inset-top, 0px) + 44px), 56px) + 8px)',
              }}
            >
              {/* Progress bars */}
              <div className="flex gap-1 w-full">
                {activeGroup.items.map((item, itemIndex) => {
                  const isPast = itemIndex < activeChildIndex;
                  const isActive = itemIndex === activeChildIndex;
                  const fillClass = isPast ? 'w-full bg-white' : isActive ? 'bg-white' : 'w-0';
                  return (
                    <span key={item.id} className="h-1 flex-1 overflow-hidden rounded-full bg-white/40">
                      <span
                        className={'block h-full rounded-full ' + fillClass + (isActive ? '' : ' transition-[width] duration-75 ease-linear')}
                        style={
                          isActive
                            ? { width: String(progress) + '%' }
                            : undefined
                        }
                      />
                    </span>
                  );
                })}
              </div>

              {/* Story Group Header Info & Close Button */}
              <div className="flex items-center justify-between w-full pt-1">
                <div className="flex items-center gap-2">
                  {activeGroup.preview && (
                    <img
                      src={activeGroup.preview}
                      alt=""
                      className="h-8 w-8 rounded-full object-cover border border-white/20 shadow-sm"
                    />
                  )}
                  <span className="text-white font-semibold text-sm drop-shadow-md truncate max-w-[220px]">
                    {activeGroup.title}
                  </span>
                </div>
                <button
                  type="button"
                  data-close-btn
                  aria-label="Close story"
                  onClick={(e) => {
                    e.stopPropagation();
                    closeStory();
                  }}
                  className="pointer-events-auto flex h-8 w-8 items-center justify-center rounded-full bg-black/40 backdrop-blur-md text-white border border-white/20 active:scale-95 transition-transform"
                >
                  <XIcon className="h-4 w-4" />
                </button>
              </div>
            </div>



            {/* Media (Video or Image) or Fallback Error View */}
            {mediaError ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center z-10">
                <div className="flex items-center justify-center w-14 h-14 rounded-full bg-white/10 text-white/60">
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3 3l18 18" />
                  </svg>
                </div>
                <p className="text-white/70 text-sm font-medium leading-snug">Не удалось<br />загрузить историю</p>
              </div>
            ) : (
              <>
                {/* Loader overlay */}
                {mediaLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black z-10">
                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-white" />
                  </div>
                )}

                {activeItem.type === 'video' ? (
                  <video
                    key={activeItem.id}
                    ref={videoRef}
                    src={mediaUrl}
                    playsInline
                    onCanPlay={() => setMediaLoading(false)}
                    onLoadedData={() => setMediaLoading(false)}
                    onLoadedMetadata={() => setMediaLoading(false)}
                    onError={() => {
                      setMediaError(true);
                      setMediaLoading(false);
                    }}
                    className="h-full w-full object-contain pointer-events-none select-none"
                  />
                ) : (
                  <img
                    key={activeItem.id}
                    src={mediaUrl}
                    alt={activeGroup.title}
                    loading="eager"
                    decoding="async"
                    onLoad={(e) => {
                      setMediaLoading(false);
                      // iOS Safari sometimes fires onLoad before paint — force flush
                      void (e.currentTarget as HTMLImageElement).decode?.();
                    }}
                    ref={(el) => {
                      // Handle already-cached images on iOS where onLoad doesn't fire
                      if (el && el.complete && el.naturalWidth > 0) {
                        setMediaLoading(false);
                      }
                    }}
                    onError={() => {
                      setMediaError(true);
                      setMediaLoading(false);
                    }}
                    className="h-full w-full object-contain pointer-events-none select-none"
                  />
                )}
              </>
            )}

            {/* Bottom action bar */}
            <div
              className="absolute inset-x-0 bottom-0 z-20 flex flex-col bg-black px-4 pt-4"
              style={{
                paddingBottom: 'calc(12px + max(var(--tg-content-safe-area-inset-bottom, 0px), var(--tg-safe-area-inset-bottom, 0px), env(safe-area-inset-bottom, 0px)))',
              }}
            >
              {activeItem.target && (
                <button
                  type="button"
                  data-action-btn
                  onClick={(e) => {
                    e.stopPropagation();
                    closeStory();
                    navigateToTarget(activeItem.target!, navigate);
                  }}
                  className="h-12 w-full rounded-[32px] bg-white/20 backdrop-blur-md px-5 text-base font-semibold text-white active:scale-[0.98] transition-transform"
                >
                  {activeItem.buttonText || 'Перейти к акции'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
